import { useContext, useEffect, useMemo, useRef } from 'react';
import clientId from '~/constants/clientId';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import DeviceContext from '~/contexts/DeviceContext';
import { useSearchRequestId } from '~/contexts/InventoryContext';
import { useReferrer } from '~/contexts/StaticContext';
import UserContext from '~/contexts/UserContext';
import type { InventoryRequestAnalytics } from '~/services/contracts';
import Cookies from '~/utilities/BrowserCookies';

export default function useGenerateAnalyticsData() {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { browserName, deviceName, screenSize } = useContext(DeviceContext);
  const { sessionId } = useContext(UserContext);
  const referrer = useReferrer();
  const searchRequestId = useSearchRequestId();

  const lastActiveSession = Cookies.get('LastActiveSearchSession') || null;
  const hasFilters = Object.keys(appliedFilters).length > 0;
  const hasActiveSession = lastActiveSession === sessionId;
  const shouldUpdateLastActiveSession = lastActiveSession !== sessionId;

  // Use refs here because we don't want to trigger new API requests when
  // analytics data changes.
  const isFirstActiveSearchSession = useRef(
    (!hasActiveSession || shouldUpdateLastActiveSession) && hasFilters
  );
  useEffect(() => {
    isFirstActiveSearchSession.current =
      (!hasActiveSession || shouldUpdateLastActiveSession) && hasFilters;
  }, [hasActiveSession, hasFilters, shouldUpdateLastActiveSession]);

  const isMobile = useRef(screenSize.isMobile);
  useEffect(() => {
    isMobile.current = screenSize.isMobile;
  }, [screenSize.isMobile]);

  const searchRequestIdRef = useRef(searchRequestId);
  useEffect(() => {
    searchRequestIdRef.current = searchRequestId;
  }, [searchRequestId]);

  return useMemo<InventoryRequestAnalytics>(
    () => ({
      browser: browserName,
      clientId,
      deviceName,
      isFirstActiveSearchSession: isFirstActiveSearchSession.current,
      isMobileDevice: isMobile.current,
      previousSearchRequestId: searchRequestIdRef.current,
      referrer
    }),
    [browserName, deviceName, referrer]
  );
}
